import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  TextInput, 
  toast,
  Select,
  ComboBox
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { FormCard } from '../../components/cards';
import { organizationService } from '../../services/organizationService';
import { ApiError } from '../../utils/errorHandling';
import type { OrganizationType } from '../../types/organization';
import { ORGANIZATION_TYPE_LABELS } from '../../types/organization';
import { validateTin } from '../../utils/tinValidation';
import { validatePhoneForSms } from '@myubuntu/phone-validation';
import {
  ChevronLeftIcon,
} from 'lucide-react';
import { COUNTRY_OPTIONS } from '../../config/countryList';

interface CreateOrganizationFormData {
  name: string;
  code: string;
  type: OrganizationType;
  email?: string; // Organization email address
  tin?: string; // Tax Identification Number
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isActive: boolean;
  // Contact person information
  contactPerson: {
    name: string;
    role: string;
    contactNumber: string;
    email: string;
  };
}

// Validation schema
const validationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  code: z.string()
    .min(2, 'Code must be at least 2 characters')
    .max(10, 'Code must be at most 10 characters')
    .regex(/^[A-Z0-9_]+$/, 'Code must be uppercase letters, numbers, and underscores only'),
  type: z.string().min(1, 'Organization type is required'),
  email: z.string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      return z.string().email().safeParse(val).success;
    }, 'Please enter a valid email address'),
  tin: z.string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const tinResult = validateTin(val.trim(), 'ZW');
      return tinResult.isValid;
    }, 'Please enter a valid Tax Identification Number'),
  phone: z.string()
    .min(1, 'Phone number is required')
    .refine((val) => {
      const phoneResult = validatePhoneForSms(val);
      return phoneResult.isValid;
    }, 'Please enter a valid international phone number (e.g., +263771234567)'),
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  isActive: z.boolean(),
  contactPerson: z.object({
    name: z.string().min(1, 'Contact person name is required'),
    role: z.string().min(1, 'Contact person role is required'),
    contactNumber: z.string()
      .min(1, 'Contact person phone number is required')
      .refine((val) => {
        const phoneResult = validatePhoneForSms(val);
        return phoneResult.isValid;
      }, 'Please enter a valid international phone number (e.g., +263771234567)'),
    email: z.string()
      .min(1, 'Contact person email is required')
      .email('Please enter a valid email address'),
  }),
});

export function CreateOrganization() {
  const navigate = useNavigate();
  const showSuccess = useCallback((title: string, description: string) => {
    return toast.success(title, { description });
  }, []);

  const showError = useCallback((title: string, description: string) => {
    return toast.error(title, { description });
  }, []);
  

  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // React Hook Form setup
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateOrganizationFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      code: '',
      type: 'OTHER',
      email: '',
      tin: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      isActive: true,
      contactPerson: {
        name: '',
        role: '',
        contactNumber: '',
        email: '',
      },
    },
  });
  
  const formData = watch();


  const onSubmit = useCallback(async (data: CreateOrganizationFormData) => {
    setIsSubmitting(true);
    
    try {
      await organizationService.createOrganization({
        name: data.name.trim(),
        code: data.code.trim().toUpperCase(),
        type: data.type,
        addressLine1: data.addressLine1?.trim() || undefined,
        addressLine2: data.addressLine2?.trim() || undefined,
        city: data.city?.trim() || undefined,
        stateProvince: data.state?.trim() || undefined,
        postalCode: data.postalCode?.trim() || undefined,
        country: data.country?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        settings: {
          email: data.email?.trim() || undefined,
          tin: data.tin?.trim() || undefined,
          contact_person: {
            name: data.contactPerson.name.trim(),
            role: data.contactPerson.role.trim(),
            contactNumber: data.contactPerson.contactNumber.trim() || undefined,
            email: data.contactPerson.email.trim() || undefined,
          },
        },
      });
      
      showSuccess('Organisation Created', `Organisation "${data.name}" has been created successfully.`);
      navigate('/admin/organizations');
    } catch (error) {
      console.error('Error creating organization:', error);
      
      if (error instanceof ApiError) {
        showError('Creation Failed', error.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create organisation.';
        showError('Creation Failed', errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [showSuccess, showError, navigate]);

  const handleCancel = useCallback(() => {
    navigate('/admin/organizations');
  }, [navigate]);

  // Organization type options for select
  const typeOptions = Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleCancel}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Organisation</h1>
          <p className="text-gray-600 mt-1">
            Add a new organisation to the system
          </p>
        </div>
      </div>

      {/* Form */}
      <FormCard title="Organisation Information">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextInput
                label="Organisation Name *"
                placeholder="Enter organisation name"
                {...register('name')}
                error={errors.name?.message}
                required
              />
              <TextInput
                label="Organisation Code *"
                placeholder="Enter organisation code (e.g., ACME_CORP)"
                {...register('code', {
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase();
                  }
                })}
                error={errors.code?.message}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Organisation Type *"
                options={typeOptions}
                value={formData.type}
                onValueChange={(value: string) => setValue('type', value as OrganizationType)}
                placeholder="Select organisation type"
                error={errors.type?.message}
              />
              <Select
                label="Status"
                options={statusOptions}
                value={formData.isActive.toString()}
                onValueChange={(value: string) => setValue('isActive', value === 'true')}
                placeholder="Select status"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextInput
                label="Organisation Email"
                placeholder="Enter organisation email (e.g., info@example.com)"
                type="email"
                {...register('email')}
                error={errors.email?.message}
              />
              <TextInput
                label="Tax Identification Number (TIN)"
                placeholder="Enter TIN (e.g., 123-456-7890)"
                {...register('tin')}
                error={errors.tin?.message}
              />
            </div>

            {/* Contact Information & Address */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium mb-4">Contact Information & Address</h3>
              
              {/* Contact Person Details */}
              <div className="mb-6">
                <h4 className="text-md font-medium mb-3 text-gray-700">Contact Person</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    label="Full Name *"
                    placeholder="Tendai Mukamuri"
                    {...register('contactPerson.name')}
                    error={errors.contactPerson?.name?.message}
                    required
                  />
                  <TextInput
                    label="Role/Title *"
                    placeholder="Operations Manager"
                    {...register('contactPerson.role')}
                    error={errors.contactPerson?.role?.message}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <TextInput
                    label="Contact Number *"
                    placeholder="+263771234567"
                    {...register('contactPerson.contactNumber')}
                    error={errors.contactPerson?.contactNumber?.message}
                    required
                  />
                  <TextInput
                    label="Email *"
                    type="email"
                    placeholder="tendai.mukamuri@company.co.zw"
                    {...register('contactPerson.email')}
                    error={errors.contactPerson?.email?.message}
                    required
                  />
                </div>
              </div>

              {/* Organization Contact & Address */}
              <div>
                <h4 className="text-md font-medium mb-3 text-gray-700">Organisation Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <TextInput
                    label="Phone *"
                    placeholder="+263771234567"
                    {...register('phone')}
                    error={errors.phone?.message}
                    required
                  />
                  <ComboBox
                    label="Country *"
                    options={COUNTRY_OPTIONS}
                    value={formData.country || ''}
                    onChange={(value: string) => setValue('country', value, { shouldValidate: true })}
                    placeholder="Select a country"
                    error={errors.country?.message}
                  />
                </div>
                <div className="space-y-4">
                  <TextInput
                    label="Address Line 1 *"
                    placeholder="123 Nelson Mandela Avenue"
                    {...register('addressLine1')}
                    error={errors.addressLine1?.message}
                    required
                  />
                  <TextInput
                    label="Address Line 2"
                    placeholder="Suite 5A"
                    {...register('addressLine2')}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="City *"
                      placeholder="Harare"
                      {...register('city')}
                      error={errors.city?.message}
                      required
                    />
                    <TextInput
                      label="State/Province"
                      placeholder="Harare Province"
                      {...register('state')}
                    />
                  </div>
                  <TextInput
                    label="Postal Code"
                    placeholder="263"
                    {...register('postalCode')}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <NeutralButton
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </NeutralButton>
              <PositiveButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingText="Creating..."
                showIcon={false}
              >
                Create Organization
              </PositiveButton>
            </div>
          </form>
      </FormCard>
    </div>
  );
} 